import bookaMeetingApi from './request';

export type LeadFormMessage = {
  at: string;
  text: string;
};

export type BusinessLead = {
  conversationId: string;
  name: string;
  whatsappNumber: string;
  instagramId: string;
  instagramCustomerId: string;
  updatedAt: string;
  connection: string;
  formFilled?: string;
  forms?: LeadFormMessage[];
  stageId?: string;
  tagIds?: string[];
  score?: number;
  formScore?: number;
  decisionScore?: number;
  scoredAt?: string;
  scoreReason?: string;
  followUpScript?: string;
  staffNote?: string;
};

export type WhatsAppThreadMessage = {
  mid: string;
  customerId: string;
  customerName: string;
  direction: string;
  source: string;
  type: string;
  text: string;
  timestamp: string;
  updatedAt: string;
};

/** One customer's WhatsApp messages, including a sent template image and its filled text. */
export async function getWhatsAppMessages(
  customerNumber: string,
  businessNumber: string
): Promise<WhatsAppThreadMessage[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    messages?: WhatsAppThreadMessage[];
  }>({
    url: '/leads/whatsapp-messages',
    method: 'GET',
    params: {
      customerNumber,
      ...(businessNumber ? { businessNumber } : {}),
    },
  });
  return response.data?.messages || [];
}

/** People saved on this business. Does not load their messages. */
export async function getBusinessLeads(input: {
  whatsappBusinessId?: string;
  instagramBusinessId?: string;
}): Promise<BusinessLead[]> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    leads?: BusinessLead[];
  }>({
    url: '/leads',
    method: 'GET',
    params: {
      ...(input.whatsappBusinessId ? { whatsappBusinessId: input.whatsappBusinessId } : {}),
      ...(input.instagramBusinessId ? { instagramBusinessId: input.instagramBusinessId } : {}),
    },
  });
  return response.data?.leads || [];
}

export type LeadPipeline = {
  stages: Array<{
    id: string;
    name: string;
    color: string;
    description: string;
    aiOn: boolean;
    requireAttribute: boolean;
    attributeName: string;
  }>;
  tags: Array<{ id: string; name: string; color: string }>;
  fields: Array<{ id: string; name: string }>;
};

/** Stages, tags, and extra columns saved for this business. */
export async function getLeadPipeline(businessId: string): Promise<LeadPipeline | null> {
  if (!businessId) return null;
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    stages?: LeadPipeline['stages'];
    tags?: LeadPipeline['tags'];
    fields?: LeadPipeline['fields'];
  }>({
    url: '/leads/pipeline',
    method: 'GET',
    params: { businessId },
  });
  if (!response.data?.success) return null;
  return {
    stages: response.data.stages || [],
    tags: response.data.tags || [],
    fields: response.data.fields || [],
  };
}

export type LeadRecoveryStep = {
  amount: string;
  unit: 'minutes' | 'hours' | 'days';
  templateId: string;
  templateName: string;
  message: string;
};

export type LeadRecoveryAnswers = {
  reachAmount: string;
  reachUnit: 'minutes' | 'hours' | 'days';
  followChannel: 'whatsapp' | 'instagram' | 'email' | 'call';
  followUps: string;
  totalTouches: string;
  dormantPercent: string;
  followSteps: LeadRecoveryStep[];
  onlyIfNoReply?: boolean;
  updatedAt?: string;
};

/** Saved lead recovery answers for this business. Empty until Continue is used. */
export async function getLeadRecovery(businessId: string): Promise<LeadRecoveryAnswers | null> {
  if (!businessId) return null;
  const response = await bookaMeetingApi.requestWIthAuth<LeadRecoveryAnswers & { success?: boolean }>({
    url: '/leads/recovery',
    method: 'GET',
    params: { businessId },
  });
  if (!response.data?.success || !response.data.updatedAt) return null;
  return response.data;
}

export type TriggerChannel = 'platform' | 'instagram' | 'whatsapp' | 'web';
export type TriggerSend = 'phone' | 'text' | 'image';

export type TriggerInput = {
  name: string;
  description: string;
};

export type TriggerRoute = 'first_message' | 'match';

export type TriggerButton = {
  title: string;
  triggerId: string;
  kind?: 'quick' | 'url';
  url?: string;
};

export type TriggerRequest = {
  id: string;
  name: string;
  text: string;
  messageText?: string;
  imageUrl?: string;
  inputs?: TriggerInput[];
  channel?: TriggerChannel;
  sends?: TriggerSend[];
  toolId?: string;
  route?: TriggerRoute | '';
  buttons?: TriggerButton[];
  templateName?: string;
  languageCode?: string;
  status: 'requested' | 'saved';
  createdAt: string;
};

/** Hourly switch and trigger requests. Missing switch means on. */
export type NumberTemplateSetting = {
  enabled: boolean;
  templateName: string;
  languageCode: string;
  imageUrl: string;
  values: string[];
  bodyCount: number;
};

export async function getSmartTriggerSettings(businessId: string): Promise<{
  enabled: boolean;
  requests: TriggerRequest[];
  numberTemplate: NumberTemplateSetting;
} | null> {
  if (!businessId) return null;
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    enabled?: boolean;
    requests?: TriggerRequest[];
    numberTemplate?: NumberTemplateSetting;
  }>({
    url: '/leads/smart-trigger',
    method: 'GET',
    params: { businessId },
  });
  if (!response.data?.success || typeof response.data.enabled !== 'boolean') return null;
  return {
    enabled: response.data.enabled,
    requests: Array.isArray(response.data.requests) ? response.data.requests : [],
    numberTemplate: {
      enabled: response.data.numberTemplate?.enabled === true,
      templateName: response.data.numberTemplate?.templateName || '',
      languageCode: response.data.numberTemplate?.languageCode || '',
      imageUrl: response.data.numberTemplate?.imageUrl || '',
      values: Array.isArray(response.data.numberTemplate?.values) ? response.data.numberTemplate.values : [],
      bodyCount: Number(response.data.numberTemplate?.bodyCount) || 0,
    },
  };
}

/** Saved on the business. When on, a new Instagram WhatsApp number receives this template. */
export async function saveNumberTemplate(
  businessId: string,
  numberTemplate: NumberTemplateSetting
): Promise<NumberTemplateSetting> {
  const response = await bookaMeetingApi.requestWIthAuth<{ success?: boolean; numberTemplate?: NumberTemplateSetting }>({
    url: '/leads/smart-trigger',
    method: 'PUT',
    data: { businessId, numberTemplate },
  });
  if (!response.data?.numberTemplate) throw new Error('Could not save the WhatsApp template');
  return response.data.numberTemplate;
}

/** Saves a trigger. Platform requests wait for approval. Channel triggers are saved for edit and delete. */
export async function saveTriggerRequest(
  businessId: string,
  input: {
    id?: string;
    name: string;
    text: string;
    messageText?: string;
    imageUrl?: string;
    inputs?: TriggerInput[];
    channel: TriggerChannel;
    sends?: TriggerSend[];
    route?: TriggerRoute;
    buttons?: TriggerButton[];
    templateName?: string;
    languageCode?: string;
  }
): Promise<TriggerRequest> {
  const response = await bookaMeetingApi.requestWIthAuth<{ success?: boolean; request?: TriggerRequest }>({
    url: '/leads/smart-trigger',
    method: 'PUT',
    data: { businessId, request: input },
  });
  if (!response.data?.request) throw new Error('Could not save the trigger request');
  return response.data.request;
}

/** Removes one saved trigger or trigger request. */
export async function deleteTriggerRequest(businessId: string, id: string): Promise<void> {
  await bookaMeetingApi.requestWIthAuth({
    url: '/leads/smart-trigger',
    method: 'PUT',
    data: { businessId, deleteId: id },
  });
}

/** Saves the hourly on/off switch. Off stops the next hourly tag and stage run for this business. */
export async function saveSmartTriggerEnabled(businessId: string, enabled: boolean): Promise<void> {
  await bookaMeetingApi.requestWIthAuth({
    url: '/leads/smart-trigger',
    method: 'PUT',
    data: { businessId, enabled },
  });
}

/** Saves the lead recovery answers, including follow-up templates and Instagram messages. */
export async function saveLeadRecovery(input: LeadRecoveryAnswers & { businessId: string }): Promise<void> {
  await bookaMeetingApi.requestWIthAuth({
    url: '/leads/recovery',
    method: 'PUT',
    data: input,
  });
}

/** Saves the note, scores that lead again, and returns the new score, reason, and follow-up. */
export async function rescoreLead(input: {
  whatsappBusinessId?: string;
  instagramBusinessId?: string;
  conversationId: string;
  note: string;
}): Promise<{ score: number; scoreReason: string; followUpScript: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    score?: number;
    scoreReason?: string;
    followUpScript?: string;
    error?: string;
  }>({
    url: '/leads/rescore',
    method: 'POST',
    data: {
      whatsappBusinessId: input.whatsappBusinessId,
      instagramBusinessId: input.instagramBusinessId,
      conversationId: input.conversationId,
      customFeedback: input.note,
    },
  });
  if (!response.data?.success) throw new Error(response.data?.error || 'Could not update the score');
  return {
    score: Number(response.data.score ?? 0),
    scoreReason: response.data.scoreReason || '',
    followUpScript: response.data.followUpScript || '',
  };
}

/** Saves or clears the WhatsApp number on this conversation. An empty value removes it. */
export async function saveLeadContact(input: {
  whatsappBusinessId?: string;
  instagramBusinessId?: string;
  conversationId: string;
  whatsappNumber: string;
  clearedNumber?: string;
}): Promise<void> {
  const response = await bookaMeetingApi.requestWIthAuth<{ success?: boolean; error?: string }>({
    url: '/leads/contact',
    method: 'PUT',
    data: input,
  });
  if (response.data && response.data.success === false) {
    throw new Error(response.data.error || 'Could not save the WhatsApp number');
  }
}

/** Saves the staff note for this conversation. The score reads it as human feedback. */
export async function saveLeadNote(input: {
  whatsappBusinessId?: string;
  instagramBusinessId?: string;
  conversationId: string;
  note: string;
}): Promise<void> {
  await bookaMeetingApi.requestWIthAuth({
    url: '/leads/note',
    method: 'PUT',
    data: input,
  });
}

/** Saves stages, tags, and extra columns for this business. */
export async function saveLeadPipeline(input: LeadPipeline & { businessId: string }): Promise<void> {
  await bookaMeetingApi.requestWIthAuth({
    url: '/leads/pipeline',
    method: 'PUT',
    data: input,
  });
}

export async function importBusinessLeads(input: {
  businessId: string;
  instagramBusinessId?: string;
  leads: Array<{ name: string; whatsappNumber: string; instagramId?: string }>;
}): Promise<{ success?: boolean; created?: number; skipped?: number; conversationId?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    created?: number;
    skipped?: number;
    conversationId?: string;
    error?: string;
  }>({
    url: '/leads/import',
    method: 'POST',
    data: input,
  });
  return response.data;
}

export async function createBusinessLead(input: {
  businessId: string;
  instagramBusinessId?: string;
  name: string;
  whatsappNumber?: string;
  instagramId?: string;
}): Promise<{ success?: boolean; conversationId?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    conversationId?: string;
    error?: string;
  }>({
    url: '/leads',
    method: 'POST',
    data: input,
  });
  return response.data;
}

export type ConnectLeadInput = {
  whatsappBusinessId: string;
  instagramBusinessId: string;
  whatsappNumber: string;
  instagramId: string;
  name: string;
};

export type ConnectLeadResult = {
  success?: boolean;
  conversationId?: string;
  personId?: string;
  error?: string;
};

/** Tell the lead table these two channels are one person for this business. */
export async function connectBusinessLead(input: ConnectLeadInput): Promise<ConnectLeadResult> {
  if (!input.whatsappNumber && !input.instagramId) return {};
  if (!input.whatsappBusinessId && !input.instagramBusinessId) return {};
  const response = await bookaMeetingApi.requestWIthAuth<ConnectLeadResult>({
    url: '/leads/connect',
    method: 'POST',
    data: input,
  });
  return response.data;
}

export type WebappEventInput = {
  businessId: string;
  whatsappBusinessId?: string;
  instagramBusinessId?: string;
  whatsappNumber?: string;
  instagramId?: string;
  conversationId?: string;
  type: 'message' | 'form' | 'event';
  name: string;
  text?: string;
  fields?: Record<string, string>;
};

/** Save a web app message, form fill, or event on the shared conversation. */
export async function saveWebappEvent(
  input: WebappEventInput
): Promise<{ success?: boolean; conversationId?: string; mid?: string; error?: string }> {
  const response = await bookaMeetingApi.requestWIthAuth<{
    success?: boolean;
    conversationId?: string;
    mid?: string;
    error?: string;
  }>({
    url: '/webapp/events',
    method: 'POST',
    data: input,
  });
  return response.data;
}
